import type { NpcArchetype, RunContractState, SectorId } from "./contracts.js";
import type { ContractDifficulty } from "./progression.js";
import { relocatePatrolGameplayPoint } from "./contract-geography.js";

import { FACTION_LOCATIONS, type FactionSectorId } from "./world-layout.js";

export const LEGACY_ESCORT_ROUTE_IDS = [
  "meridian-lane",
  "patrol-bypass",
  "wrecks-relief",
] as const;
export const TERRITORY_ESCORT_ROUTE_IDS = [
  "azure-supply",
  "forge-convoy",
  "magpie-trail",
] as const;
export const ESCORT_ROUTE_IDS = [
  ...LEGACY_ESCORT_ROUTE_IDS,
  ...TERRITORY_ESCORT_ROUTE_IDS,
] as const;
export type EscortRouteId = (typeof ESCORT_ROUTE_IDS)[number];
export interface EscortWave {
  at: number;
  archetypes: NpcArchetype[];
}
export interface EscortTerms {
  routeId: EscortRouteId;
  version: "escort-routes-v1";
  path: { x: number; y: number }[];
  speed: number;
  transportHp: number;
  escortRadius: number;
  bindUntilRatio: number;
  minimumDistanceRatio: number;
  minimumContribution: number;
  waves: EscortWave[];
}
export interface EscortProof {
  routeId: EscortRouteId;
  encounterId: string;
  distance: number;
  damage: number;
  healing: number;
}
export const ESCORT_MILESTONES = [
  "escortBind",
  "escortQualify",
  "escortArrived",
  "escortLost",
] as const;
export type EscortMilestone = (typeof ESCORT_MILESTONES)[number];
export function isEscortMilestone(value: string): value is EscortMilestone {
  return ESCORT_MILESTONES.some((milestone) => milestone === value);
}
export interface EscortProgress extends EscortProof {
  phase: "bound" | "qualified" | "arrived" | "lost";
}
export interface EscortMissionView {
  status: "waiting" | "active" | "confirming" | "arrived" | "lost" | "cooldown";
  encounterId: string | null;
  transportId: string | null;
  transportHp: number;
  transportMaxHp: number;
  distanceTravelled: number;
  routeLength: number;
  escortedDistance: number;
  damage: number;
  healing: number;
}
export interface EscortRouteDefinition {
  title: string;
  description: string;
  sectorId: SectorId;
  terms: Omit<EscortTerms, "minimumDistanceRatio" | "minimumContribution">;
}
function territoryEscortRoute(
  routeId: (typeof TERRITORY_ESCORT_ROUTE_IDS)[number],
  sectorId: FactionSectorId,
  title: string,
  waves: EscortWave[],
): EscortRouteDefinition {
  const location = FACTION_LOCATIONS.find((item) => item.id === sectorId);
  if (!location || location.escortPath.length < 2)
    throw new Error(`Missing convoy path ${sectorId}`);
  return {
    title,
    sectorId,
    description: `Проведи транспорт через область «${location.name}» и три засады. Здесь возможны атаки других фракций.`,
    terms: {
      routeId,
      version: "escort-routes-v1",
      path: location.escortPath.map((point) => ({ ...point })),
      speed: 55,
      transportHp: 650,
      escortRadius: 300,
      bindUntilRatio: 0.15,
      waves,
    },
  };
}
export const ESCORT_ROUTES: Record<EscortRouteId, EscortRouteDefinition> = {
  "azure-supply": territoryEscortRoute(
    "azure-supply",
    "concord-azure",
    "Лазурное снабжение",
    [
      { at: 0.18, archetypes: ["raider"] },
      { at: 0.45, archetypes: ["sniper", "raider"] },
      { at: 0.75, archetypes: ["support", "sniper"] },
    ],
  ),
  "forge-convoy": territoryEscortRoute(
    "forge-convoy",
    "helion-forge",
    "Конвой к Горнилу",
    [
      { at: 0.18, archetypes: ["charger"] },
      { at: 0.45, archetypes: ["minelayer", "raider"] },
      { at: 0.75, archetypes: ["charger", "minelayer"] },
    ],
  ),
  "magpie-trail": territoryEscortRoute(
    "magpie-trail",
    "nomads-magpie",
    "Караван Сороки",
    [
      { at: 0.18, archetypes: ["raider", "raider"] },
      { at: 0.45, archetypes: ["disruptor", "raider"] },
      { at: 0.75, archetypes: ["sniper", "disruptor"] },
    ],
  ),
  "meridian-lane": {
    title: "Грузовой коридор «Меридиана»",
    description:
      "Проведи транспорт через поле обломков и три пиратские засады. Здесь возможны атаки других фракций.",
    sectorId: "wrecks",
    terms: {
      routeId: "meridian-lane",
      version: "escort-routes-v1",
      path: [
        { x: 1320, y: 1050 },
        { x: 1700, y: 900 },
        { x: 2200, y: 900 },
        { x: 2770, y: 1100 },
      ].map(relocatePatrolGameplayPoint),
      speed: 55,
      transportHp: 600,
      escortRadius: 300,
      bindUntilRatio: 0.15,
      waves: [
        { at: 0.18, archetypes: ["raider"] },
        { at: 0.45, archetypes: ["raider", "disruptor"] },
        { at: 0.75, archetypes: ["sniper", "raider"] },
      ],
    },
  },
  "patrol-bypass": {
    title: "Южный обход обломков",
    description:
      "Сопроводи транспорт по южной дуге поля обломков: длинные открытые участки и три засады у поворотов. Здесь возможны атаки других фракций.",
    sectorId: "wrecks",
    terms: {
      routeId: "patrol-bypass",
      version: "escort-routes-v1",
      path: [
        { x: 1260, y: 1450 },
        { x: 1510, y: 1820 },
        { x: 2130, y: 1950 },
        { x: 2660, y: 2070 },
      ].map(relocatePatrolGameplayPoint),
      speed: 55,
      transportHp: 600,
      escortRadius: 300,
      bindUntilRatio: 0.15,
      waves: [
        { at: 0.18, archetypes: ["raider"] },
        { at: 0.45, archetypes: ["minelayer", "raider"] },
        { at: 0.75, archetypes: ["charger", "raider"] },
      ],
    },
  },
  "wrecks-relief": {
    title: "Снабжение через обломки",
    description:
      "Проведи транспорт снабжения по южной кромке поля обломков и сохрани его через три засады. Здесь возможны атаки других фракций.",
    sectorId: "wrecks",
    terms: {
      routeId: "wrecks-relief",
      version: "escort-routes-v1",
      path: [
        { x: 3700, y: 1400 },
        { x: 3900, y: 1750 },
        { x: 4700, y: 1930 },
        { x: 5550, y: 2070 },
        { x: 6100, y: 1900 },
      ],
      speed: 55,
      transportHp: 650,
      escortRadius: 300,
      bindUntilRatio: 0.15,
      waves: [
        { at: 0.18, archetypes: ["raider", "raider"] },
        { at: 0.45, archetypes: ["sniper", "raider"] },
        { at: 0.75, archetypes: ["disruptor", "raider"] },
      ],
    },
  },
};

export function createEscortTerms(
  routeId: EscortRouteId,
  difficulty: ContractDifficulty,
): EscortTerms {
  const terms = ESCORT_ROUTES[routeId].terms;
  return {
    ...terms,
    path: terms.path.map((point) => ({ ...point })),
    waves: terms.waves.map((wave) => ({
      ...wave,
      archetypes: [...wave.archetypes],
    })),
    minimumDistanceRatio: difficulty === "advanced" ? 0.8 : 0.55,
    minimumContribution: difficulty === "advanced" ? 25 : 0,
  };
}

export function escortRouteLength(terms: EscortTerms): number {
  return terms.path.reduce((length, point, index) => {
    const previous = terms.path[index - 1];
    return previous
      ? length + Math.hypot(point.x - previous.x, point.y - previous.y)
      : length;
  }, 0);
}

/** Personal eligibility never changes the shared transport's physical identity. */
export function escortPhysicalKey(terms: EscortTerms): string {
  return JSON.stringify({
    routeId: terms.routeId,
    version: terms.version,
    path: terms.path,
    speed: terms.speed,
    transportHp: terms.transportHp,
    waves: terms.waves,
  });
}

function validProof(terms: EscortTerms, proof: EscortProof): boolean {
  return (
    proof.routeId === terms.routeId &&
    proof.encounterId.length > 0 &&
    proof.encounterId.length <= 200 &&
    [proof.distance, proof.damage, proof.healing].every(
      (value) => Number.isFinite(value) && value >= 0 && value <= 1_000_000_000,
    ) &&
    proof.distance <= escortRouteLength(terms) + 0.001
  );
}

export function escortEligible(
  terms: EscortTerms,
  proof: EscortProof,
): boolean {
  const length = escortRouteLength(terms);
  return (
    validProof(terms, proof) &&
    length > 0 &&
    proof.distance >= length * terms.minimumDistanceRatio &&
    proof.damage + proof.healing >= terms.minimumContribution
  );
}

export function applyEscortProof(
  contract: RunContractState,
  milestone: EscortMilestone,
  proof: EscortProof,
): RunContractState | null {
  const terms = contract.terms.escort;
  if (
    contract.contractId !== "escort" ||
    !terms ||
    contract.stage === "settled" ||
    contract.outcome !== "active" ||
    !validProof(terms, proof)
  )
    return null;
  const current = contract.escort;
  if (current?.phase === "arrived" || current?.phase === "lost") {
    const repeated =
      milestone ===
        (current.phase === "arrived" ? "escortArrived" : "escortLost") &&
      current.encounterId === proof.encounterId &&
      current.routeId === proof.routeId &&
      current.distance === proof.distance &&
      current.damage === proof.damage &&
      current.healing === proof.healing;
    return repeated ? contract : null;
  }
  if (milestone === "escortBind") {
    if (proof.distance !== 0 || proof.damage !== 0 || proof.healing !== 0)
      return null;
    if (current?.encounterId === proof.encounterId)
      return current.phase === "bound" ? contract : null;
    return { ...contract, escort: { ...proof, phase: "bound" } };
  }
  if (
    !current ||
    current.encounterId !== proof.encounterId ||
    current.routeId !== proof.routeId ||
    proof.distance < current.distance ||
    proof.damage < current.damage ||
    proof.healing < current.healing
  )
    return null;
  if (milestone === "escortQualify")
    return escortEligible(terms, proof)
      ? { ...contract, escort: { ...proof, phase: "qualified" } }
      : null;
  if (milestone === "escortLost")
    return {
      ...contract,
      stage: "return",
      escort: { ...proof, phase: "lost" },
    };
  if (current.phase !== "qualified" || !escortEligible(terms, proof))
    return null;
  return {
    ...contract,
    progress: contract.goal,
    stage: "return",
    escort: { ...proof, phase: "arrived" },
  };
}
